import { CacheProvider } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import createCache from '@emotion/cache';
import { t } from '../../../../i18n';
import { registerActiveWindow, unregisterActiveWindow } from '../../../../Utils/DetachedWindowManager';
import { windowFocusManager } from '../../../../Utils/WindowFocusManager';

interface DetachedTournamentLobbyProps {
  children: React.ReactNode;
  onClose?: () => void;
  title?: string;
  width?: number;
  height?: number;
}

function createEmotionCache(container: HTMLElement) {
  return createCache({
    key: 'mui',
    prepend: true,
    container, // Emotion will inject styles here
  });
}

const DetachedTournamentLobby: React.FC<DetachedTournamentLobbyProps> = ({
  children,
  onClose,
  title = t('Tournament:title'),
  width = 1200,
  height = 760,
}) => {
  const newWindowRef = useRef<Window | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const windowNameRef = useRef(`tournament-lobby-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const emotionCacheRef = useRef<ReturnType<typeof createCache> | null>(null);

  useEffect(() => {
    containerRef.current = document.createElement("div");

    newWindowRef.current = window.open(
      "",
      windowNameRef.current,
      `width=${width},height=${height},left=200,top=200,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no`
    );

    if (newWindowRef.current) {
      const newDoc = newWindowRef.current.document;

      const fontLink = newDoc.createElement("link");
      fontLink.rel = "stylesheet";
      fontLink.href =
        "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap";
      newDoc.head.appendChild(fontLink);

      // Add custom body styles
      const styleTag = newDoc.createElement("style");
      styleTag.textContent = `
        body {
          font-family: 'Inter', sans-serif;
          margin: 0;
          padding: 0;
          background-color: #191A1F;
          color: #ffffff;
        }
      `;
      newDoc.head.appendChild(styleTag);

      // Set title
      newDoc.title = title;

      // Add React container
      newDoc.body.appendChild(containerRef.current);

      // Handle close
      const curWindow = newWindowRef.current;
      const windowId = windowNameRef.current;
      const handleUnload = () => onClose?.();
      curWindow.addEventListener("beforeunload", handleUnload);
      windowFocusManager.registerWindow(windowId, curWindow, title, { tileable: false });
      registerActiveWindow(windowId, curWindow);

      setMounted(true);

      return () => {
        curWindow.removeEventListener("beforeunload", handleUnload);
        windowFocusManager.unregisterWindow(windowId);
        unregisterActiveWindow(windowId);
        curWindow.close();
      };
    }
  }, []);

  if (!mounted || !containerRef.current) return null;

  if (!emotionCacheRef.current && newWindowRef.current) {
    emotionCacheRef.current = createEmotionCache(newWindowRef.current.document.head);
  }

  return ReactDOM.createPortal(
    <CacheProvider value={emotionCacheRef.current!}>
      {children}
    </CacheProvider>,
    containerRef.current
  );
};

export default DetachedTournamentLobby;
